import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BookOpen,
  BriefcaseBusiness,
  Circle,
  ClipboardCheck,
  CreditCard,
  Download,
  FileText,
  GraduationCap,
  HelpCircle,
  Inbox,
  LayoutDashboard,
  LayoutTemplate,
  MessageSquareMore,
  Package,
  Settings,
  UserCheck,
  UserCircle,
  Users,
  UserCog,
} from 'lucide-react';
import { adminApi } from '../services/api.jsx';
import { useDroits } from '../context/Droits';

const NOTIFICATION_POLL_MS = 25000;

/* ── Item principal ──────────────────────────────────────────────── */
const NavItem = ({ icon: Icon, label, active, onClick, collapsed, badgeCount = 0 }) => (
  <button
    onClick={onClick}
    /* Réduite, l'entrée n'a plus de libellé : son nom accessible viendrait à
       manquer, et le lecteur d'écran annoncerait un bouton vide. `title` sert
       en plus d'infobulle à la souris — sans elle, une colonne d'icônes se
       devine au lieu de se lire. */
    title={collapsed ? label : undefined}
    aria-label={collapsed ? label : undefined}
    /* L'entrée active est une CARTE BLANCHE marquée d'un trait vert à gauche,
       pas un aplat vert. C'est la forme du modèle, et elle se tient mieux :
       un aplat d'accent sur dix entrées transforme le menu en damier, et le
       libellé y perd le contraste que le fond clair lui donnait. */
    className={`relative w-full flex items-center rounded-controle transition-all duration-200 group
      min-h-[40px] ${collapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2'}
      ${active
        ? 'bg-[var(--bg-card)] shadow-carte text-[var(--text-bold)]'
        : 'text-[var(--text-main)] hover:bg-[var(--bg-main)] hover:text-[var(--text-bold)]'}`}
  >
    {active && !collapsed && (
      <span aria-hidden="true"
            className="absolute -left-3 top-2.5 bottom-2.5 w-1 rounded-r bg-[var(--accent)]" />
    )}

    <div className="relative shrink-0">
      <Icon className="w-[18px] h-[18px]" strokeWidth={1.5} />
      {collapsed && badgeCount > 0 && (
        <span className="absolute -top-1.5 -right-1.5 w-2 h-2 rounded-controle bg-[var(--accent)]" />
      )}
    </div>

    {!collapsed && (
      <span className="text-[13px] font-semibold leading-tight text-left">{label}</span>
    )}

    {!collapsed && badgeCount > 0 && (
      <span className="ml-auto min-w-6 h-5 px-[7px] shrink-0 rounded-controle text-[12px] font-semibold
                       grid place-items-center bg-[var(--vert-pale)] text-[var(--vert-fonce)]">
        {badgeCount > 99 ? '99+' : badgeCount}
      </span>
    )}
  </button>
);

/* `NavGroup` et `SubNavItem` ont ete retires. Ils repliaient « Gestion »
   derriere un accordeon : sur un menu qui compte desormais quatre sections
   nommees, replier l'une d'elles cache ce qu'on vient chercher et ajoute un
   clic a chaque visite. Les sections sont a plat, leur titre suffit. */

/* Le serveur nomme l'icone de chaque zone ; il ne peut pas envoyer un
   composant React. Cette table fait la traduction, et c'est son seul role.
   Une icone inconnue retombe sur un rond neutre plutot que de faire planter
   le menu entier — une zone ajoutee sans son icone doit rester cliquable. */
const ICONES = {
  LayoutDashboard, BookOpen, ClipboardCheck, Users, CreditCard, Inbox,
  BriefcaseBusiness, UserCog, GraduationCap, Package, UserCheck,
  LayoutTemplate, MessageSquareMore, Settings, HelpCircle, UserCircle,
};

const iconeDe = (nom) => ICONES[nom] || Circle;

/* La page courante, telle que l'URL la nomme, ramenee a la cle de zone. */

/* Correspondance entre la page de l'interface et la zone declaree par le
   serveur. Elles portent des noms differents pour des raisons historiques
   (« users » cote interface, « organisations » cote table des zones) : ce
   tableau est le seul endroit ou l'ecart est reconcilie. */

const PAGE_DE_LA_ZONE = {
  profil: 'profile',
  organisations: 'users',
  paiements: 'payments',
  formation: 'formation-admin',
  kit: 'kit-admin',
  parametres: 'settings',
  aide: 'help',
};

export default function Sidebar({ isOpen, activePage, setPage, role, isMobile, user, onClose }) {
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [pendingDemandes, setPendingDemandes] = useState(0);

  /* Le menu etait ecrit en dur et ne gardait que « Gestion Admins » derriere
     un test de role. Un coach y voyait donc Referentiel, Organisations et
     Paiements, dont le serveur lui refusait ensuite l'ouverture : la page
     s'affichait pour finir sur « Acces reserve a l'equipe ASSO-PRO ».

     Les entrees sont desormais derivees de /users/me/zones/, c'est-a-dire de
     la table qui autorise reellement l'acces. Le menu et le controle ne
     peuvent plus diverger puisqu'ils lisent la meme declaration.
     La lecture elle-même est partagée : le menu, le garde de route et les
     boutons d'export lisaient chacun /users/me/zones/ de leur côté — trois
     requêtes pour une réponse, et trois occasions de diverger. */
  const { zones: entrees } = useDroits();
  const zones = useMemo(() => new Set(entrees.map((z) => z.cle)), [entrees]);

  /* Les SECTIONS viennent elles aussi du serveur. Le menu les codait en dur —
     « Accompagnement », « Administration » — et les entrées avec : ajouter une
     zone demandait de toucher deux fichiers, et d'y penser deux fois.

     L'ordre des sections suit celui de la table des zones : la première
     rencontrée s'affiche en premier. C'est donc `permissions.py` qui décide
     de la structure du menu, et lui seul. */
  const sections = useMemo(() => {
    const ordre = [];
    const parGroupe = new Map();
    entrees.forEach((z) => {
      const groupe = z.groupe || '';
      if (!parGroupe.has(groupe)) { parGroupe.set(groupe, []); ordre.push(groupe); }
      parGroupe.get(groupe).push(z);
    });
    return ordre.map((groupe) => ({ groupe, items: parGroupe.get(groupe) }));
  }, [entrees]);

  /* Un coach n'a ni la zone « messages » ni la zone « demandes » : les sonder
     toutes les 25 secondes ne produisait que des refus du serveur. */
  const voitMessages = zones.has('messages');
  const voitDemandes = zones.has('demandes');

  const loadUnreadMessages = useCallback(async () => {
    if (!user?.id || !voitMessages) return;
    try {
      const data = await adminApi.getAdminNotifications(user.id, 1);
      setUnreadMessages(Number(data?.unread_count) || 0);
    } catch (error) {
      console.error('Erreur chargement messages sidebar:', error);
    }
  }, [user?.id, voitMessages]);

  const loadPendingDemandes = useCallback(async () => {
    if (!voitDemandes) return;
    try {
      const data = await adminApi.getDemandes('statut=en_attente');
      const list = Array.isArray(data) ? data : (data?.results || []);
      setPendingDemandes(list.length);
    } catch {
      // silencieux si l'endpoint n'existe pas encore
    }
  }, [voitDemandes]);

  useEffect(() => {
    loadUnreadMessages();
    loadPendingDemandes();
    const intervalId = setInterval(() => {
      loadUnreadMessages();
      loadPendingDemandes();
    }, NOTIFICATION_POLL_MS);
    return () => clearInterval(intervalId);
  }, [loadUnreadMessages, loadPendingDemandes]);

  /* TROIS ÉTATS, ET UN SEUL À LA FOIS.

     - ouverte, 256 px : libellés visibles, c'est l'état de travail ;
     - réduite, 68 px : icônes seules, pour rendre de la largeur au contenu ;
     - tiroir, sous 1024 px : hors de l'écran, ramenée par-dessus le contenu.

     La largeur réduite valait 96 px, ce qui n'était ni l'un ni l'autre : trop
     large pour une colonne d'icônes, trop étroite pour un libellé. Une icône
     de 20 px centrée dans 68 px laisse 24 px de part et d'autre — la même
     respiration que dans l'état ouvert, sans le vide.

     Fermé, le tiroir est rendu invisible plutôt que seulement déplacé hors du
     cadre. Un élément simplement translaté reste tabulable : la tabulation
     partait visiter quinze entrées invisibles avant d'atteindre la page.
     `visibility: hidden` l'en retire nativement — et, contrairement à
     l'attribut `inert`, cela ne dépend pas de la version de React ni du
     navigateur. La visibilité est rétablie au début de la transition
     d'ouverture, sans quoi le tiroir n'apparaîtrait qu'à son terme. */
  const largeur = isMobile ? 256 : (isOpen ? 256 : 68);
  const cache = isMobile && !isOpen;

  useEffect(() => {
    if (!isMobile || !isOpen || !onClose) return undefined;
    const auClavier = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', auClavier);
    return () => document.removeEventListener('keydown', auClavier);
  }, [isMobile, isOpen, onClose]);

  return (
    <aside
      aria-label="Navigation principale"
      aria-hidden={cache || undefined}
      style={{ width: largeur,
        visibility: cache ? 'hidden' : 'visible',
        transitionDelay: cache ? '0s, 0s, 300ms' : '0s' }}
      /* La barre n'est plus collée au bord de l'écran : elle est une CARTE
         posée dans le cadre, comme dans le modèle. Sur mobile elle
         redevient un tiroir flottant — c'est le seul cas où elle sort du
         flux et où elle a donc besoin d'être fixée. */
      className={`flex flex-col bg-[var(--bg-side)] rounded-carte z-50
        transition-[transform,width,visibility] duration-300
        ${isMobile
          ? `fixed top-2 bottom-2 left-2 shadow-[0_30px_80px_-20px_rgba(11,22,32,0.45)]
             ${isOpen ? 'translate-x-0' : '-translate-x-[110%]'}`
          /* `h-full` : la barre remplit le cadre, qui a desormais une
             hauteur definie. Elle calculait la sienne sur la FENETRE
             (`100vh - 1.25rem`), soit 24 px de plus que la place
             disponible : elle depassait le cadre, etirait la rangee, et
             le graphique voisin se retrouvait mesure a zero de large.
             Deux hauteurs a tenir accordees, c'est une de trop. */
          : 'h-full translate-x-0 shrink-0'}`}>

      <div className={`h-24 flex items-center gap-4 border-b border-[var(--border)] shrink-0
                       ${isOpen ? 'px-6' : 'px-0 justify-center'}`}>
        {/* La marque compacte, reprise du favicon : « AS », A blanc et S vert
            sur le bleu nuit. Un « A » seul ne disait rien de la marque. */}
        <svg viewBox="0 0 32 32" role="img" aria-label="ASSO-PRO"
             className="w-12 h-12 shrink-0 shadow-xl shadow-[0_14px_28px_rgba(16,37,56,0.28)] rounded-pastille">
          <rect width="32" height="32" rx="9" fill="#102538" />
          <text x="16" y="22.2" textAnchor="middle" fontSize="16" fontWeight="700"
                letterSpacing="-0.6" fontFamily="Geist, system-ui, sans-serif">
            <tspan fill="#FFFFFF">A</tspan><tspan fill="#5EBD4F">S</tspan>
          </text>
        </svg>
        {isOpen && (
          <span className="min-w-0">
            <span className="block text-[17px] font-semibold tracking-[-.02em] text-[var(--text-bold)] whitespace-nowrap">
              ASSO-PRO
            </span>
            <span className="block text-[12px] text-[var(--text-muted)] whitespace-nowrap">
              Console d'administration
            </span>
          </span>
        )}
      </div>

      {/* La hauteur du menu était calculée en retranchant 280 px — une valeur
          qui ne correspondait à rien de mesurable et que le moindre ajout au
          pied de page rendait fausse. En colonne flexible, le menu prend ce
          qui reste, quoi qu'il y ait au-dessus et au-dessous. */}
      <nav className={`flex-1 min-h-0 mt-6 space-y-1 overflow-y-auto overflow-x-hidden
                       ${isOpen ? 'px-4' : 'px-2'}`}>
        {/* Une section par groupe declare, dans l'ordre du serveur.
            Le titre disparait quand la barre est reduite : un intitule de
            section dans 68 px se tronque a deux lettres, ce qui n'informe
            plus. Un filet le remplace — il separe sans pretendre nommer. */}
        {sections.map(({ groupe, items }) => (
          <div key={groupe || 'sans-groupe'}>
            {groupe && (isOpen
              ? <p className="px-2 pt-4 pb-1 surtitre text-[var(--text-muted)] opacity-60">{groupe}</p>
              : <div className="my-3 border-t border-[var(--border)] opacity-40" />
            )}
            <div className="space-y-1">
              {items.map((zone) => {
                const page = PAGE_DE_LA_ZONE[zone.cle] || zone.cle;
                const badge = zone.cle === 'messages' ? unreadMessages
                            : zone.cle === 'demandes' ? pendingDemandes
                            : 0;
                return (
                  <NavItem
                    key={zone.cle}
                    icon={iconeDe(zone.icone)}
                    label={zone.libelle}
                    active={activePage === page}
                    onClick={() => setPage(page)}
                    collapsed={!isOpen}
                    badgeCount={badge}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* ── La carte du modèle, en pied de barre ──────────────────────
          Un seul appel à l'action, sombre, dans une colonne autrement
          claire : c'est la seule chose qu'on propose de faire depuis la
          navigation. Elle disparaît quand la barre est réduite — à 68 px
          elle ne serait qu'un carré sombre sans texte.

          Le bouton mène à la liste des diagnostics, d'où le rapport se
          télécharge. */}
      {isOpen && (
        <div className="carte-pied-barre shrink-0 mx-3 mb-3 rounded-carte p-4 relative overflow-hidden sur-sombre text-white"
             style={{ background: 'radial-gradient(120% 130% at 100% 100%, #1E5B3B 0%, #12304A 48%, #0B1620 100%)' }}>
          <svg width="200" height="200" viewBox="0 0 200 200" aria-hidden="true"
               className="absolute -right-[80px] -bottom-[90px] pointer-events-none">
            {[30, 48, 66, 84].map((r, i) => (
              <circle key={r} cx="100" cy="100" r={r} fill="none"
                      stroke={`rgba(94,189,79,${[0.32, 0.22, 0.14, 0.09][i]})`} />
            ))}
          </svg>
          {/* L'icône sur la MÊME ligne que le titre. Empilés, ils
              coûtaient 44 px de hauteur à une carte qui vit en bas d'une
              colonne déjà pleine — et la barre latérale n'a de hauteur
              que celle de l'écran. */}
          <div className="relative flex items-center gap-2.5">
            <span className="w-8 h-8 shrink-0 rounded-controle grid place-items-center bg-white/10">
              <FileText className="w-[18px] h-[18px] text-[var(--accent)]" strokeWidth={1.8} />
            </span>
            <p className="m-0 text-[15px] font-semibold tracking-[-.01em]">
              Dernier rapport
            </p>
          </div>
          <p className="relative m-0 mt-2 mb-3 text-[12.5px] text-white/[.72]">
            Synthèse d'un diagnostic, prête à partager.
          </p>
          <button
            type="button"
            onClick={() => setPage('diagnostics')}
            className="relative w-full h-[38px] rounded-controle bg-[var(--accent)] text-[#0B1620]
                       text-[13px] font-semibold flex items-center justify-center gap-2
                       hover:brightness-95 transition-[filter]"
          >
            <Download className="w-4 h-4" strokeWidth={2} />
            Télécharger le PDF
          </button>
        </div>
      )}

      {/* La barre s'arrête sur la carte, comme dans le modèle.
          L'identité et la déconnexion vivaient ici EN DOUBLE : elles sont
          déjà dans le menu du compte, en haut à droite, qui reste visible
          quand la barre est repliée ou fermée en tiroir — ce qui n'était
          pas le cas de cette zone. */}
    </aside>
  );
}
